import React, { useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchSelect } from '@/components/po/SearchSelect';
import apiClient from '@/services/apiClient';

const BLANK = { vendor_name: '', contact: '', gst_no: '', state: '', address: '' };

/** Quick-add a vendor from Create PO — the state decides CGST + SGST (Tamil Nadu) vs IGST on its POs. */
export const AddVendorDialog = ({ open, states, stateCodes, onClose, onCreated }) => {
  const [v, setV] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(BLANK); }, [open]);
  const setGst = (gst) => setV((x) => {
    const auto = stateCodes[(gst || '').trim().slice(0, 2)];
    return { ...x, gst_no: gst, state: auto && (!x.state || x.state === stateCodes[(x.gst_no || '').trim().slice(0, 2)]) ? auto : x.state };
  });
  const save = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post('/purchase-orders/vendors', v);
      toast.success(`Vendor created: ${r.data.vendor.vendor_name} (${r.data.vendor.vendor_code})`);
      onCreated(r.data.vendor);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create vendor'); } finally { setBusy(false); }
  };
  const ok = v.vendor_name.trim().length >= 2 && v.state;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-vendor-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-indigo-600" />Add New Vendor</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs text-gray-500">Vendor Name *</Label>
            <Input autoFocus value={v.vendor_name} onChange={(e) => setV({ ...v, vendor_name: e.target.value })} placeholder="e.g., Sri Lakshmi Textiles, Salem" data-testid="new-vendor-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Contact</Label>
              <Input value={v.contact} onChange={(e) => setV({ ...v, contact: e.target.value })} placeholder="Phone / email" data-testid="new-vendor-contact" /></div>
            <div><Label className="text-xs text-gray-500">GST No.</Label>
              <Input value={v.gst_no} onChange={(e) => setGst(e.target.value.toUpperCase())} placeholder="Optional" data-testid="new-vendor-gst" /></div>
          </div>
          <div><Label className="text-xs text-gray-500">State *</Label>
            <SearchSelect value={v.state} options={states.map((s) => ({ value: s, label: s }))} onChange={(s) => setV({ ...v, state: s })}
              placeholder="Select state" searchPlaceholder="Search state…" testId="new-vendor-state" />
            <p className="mt-1 text-[11px] text-gray-400">Tamil Nadu → CGST + SGST on the PO, any other state → IGST. Filled from the GST No. when it is typed.</p></div>
          <div><Label className="text-xs text-gray-500">Address</Label>
            <Textarea value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} placeholder="Optional" rows={2} data-testid="new-vendor-address" /></div>
          <p className="text-xs italic text-gray-400">Vendor code is auto-generated (V-00001, V-00002 …)</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-vendor-btn">Cancel</Button>
          <Button onClick={save} disabled={busy || !ok} data-testid="save-vendor-btn">{busy ? 'Creating…' : 'Create Vendor'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddVendorDialog;
